import React, { useState, useEffect } from 'react';
import {
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  Radar, Legend, ResponsiveContainer,
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell,
  ComposedChart, Line, Scatter, Brush,
  ReferenceLine, LabelList,
  LineChart, ScatterChart, ZAxis
} from 'recharts';

const API = "/api";
const isMobile = () => window.innerWidth < 768;

const RISK_COLORS = {
  Low: '#4ADE80',
  Elevated: '#FBBF24',
  High: '#F87171',
};

const TOURNAMENT_ALIASES = {
  'cincinnati': 'Cincinnati Masters',
  'canada': 'Canada Masters',
  'montreal': 'Canada Masters',
  'toronto': 'Toronto',
  'indian wells': 'Indian Wells Masters',
  'miami': 'Miami Masters',
  'madrid': 'Madrid Masters',
  'rome': 'Rome Masters',
  'paris': 'Paris Masters',
  'monte carlo': 'Monte Carlo Masters',
  'shanghai': 'Shanghai Masters',
  'roland garros': 'Roland Garros',
  'french open': 'Roland Garros',
};

function normalizeTournaments(tournaments) {
  if (!tournaments) return tournaments;
  return tournaments.split(',').map(t => {
    const lower = t.trim().toLowerCase();
    for (const [alias, canonical] of Object.entries(TOURNAMENT_ALIASES)) {
      if (lower === alias || lower.startsWith(alias)) return canonical;
    }
    return t.trim();
  }).join(',');
}

/**
 * Resolve a possibly-truncated player name to canonical form.
 * Returns { name, tour } where tour may be null if not resolved.
 */
async function resolvePlayer(name, fallback) {
  if (!name) return { name: fallback || null, tour: null };
  // Always call resolve API to get tour, even for full names
  const queryName = name.includes(' ') ? name : name;
  try {
    const res = await fetch(`${API}/player/resolve/${encodeURIComponent(queryName)}`);
    const data = await res.json();
    if (data.canonical) return { name: data.canonical, tour: data.tour || null };
  } catch (_) {}
  // Fallback: if resolve fails for a full name, return it as-is
  if (name.includes(' ')) return { name, tour: null };
  return { name: fallback || name, tour: null };
}

function RadarChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => {
    resolvePlayer(player, fallback).then(setResolved);
  }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/radar?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.dimensions) setData(d.dimensions); })
      .catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">Physical Profile — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={280}>
        <RadarChart data={data} margin={{top:10, right:20, bottom:10, left:20}}>
          <PolarGrid stroke="var(--border)" />
          <PolarAngleAxis dataKey="axis" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
          <Radar dataKey="value" stroke="var(--accent-bright)" fill="var(--accent-bright)" fillOpacity={0.25} strokeWidth={2} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

function TimelineChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => {
    resolvePlayer(player, fallback).then(setResolved);
  }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/timeline?tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.data && d.data.length > 0) setData(d.data); })
      .catch(() => {});
  }, [resolved, effectiveTour]);

  if (!data) return null;

  const hasWalkovers = data.some(d => d.walkover > 0);

  return (
    <div className="chart-container">
      <div className="chart-title">Career Activity — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={320}>
        <ComposedChart data={data} barCategoryGap="0%" barGap={0} margin={{top:5, right:10, bottom:5, left:-10}}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis
            dataKey="week"
            tick={{ fontSize: 9, fill: 'var(--text-secondary)' }}
            interval={51}
            tickFormatter={(val) => val ? val.substring(0, 7) : ''}
          />
          <YAxis tick={{ fontSize: 9, fill: 'var(--text-secondary)' }} width={25} />
          <Tooltip
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
            formatter={(value, name) => {
              if (name === 'Break') return value > 0 ? ['In break', 'Status'] : [null, null];
              if (name === 'Retirement') return value > 0 ? ['Yes', 'Retirement'] : [null, null];
              if (name === 'Walkover') return value > 0 ? ['Yes', 'Walkover'] : [null, null];
              return [value, name];
            }}
          />
          <Legend
            wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }}
            iconSize={8}
          />
          <Bar dataKey="matches" name="Matches" fill="#7B74E0" opacity={0.8} barSize={4} />
          <Bar dataKey="break_marker" name="Break" fill="#F87171" opacity={0.5} barSize={4} />
          <Scatter
            dataKey="retirement"
            name="Retirement"
            fill="#F87171"
            shape={(props) => {
              if (!props.payload || !props.payload.retirement) return null;
              return <circle cx={props.cx} cy={props.cy} r={5} fill="#F87171" stroke="none" />;
            }}
          />
          {hasWalkovers && (
            <Scatter
              dataKey="walkover"
              name="Walkover"
              fill="#AFA9EC"
              shape={(props) => {
                if (!props.payload || !props.payload.walkover) return null;
                return <circle cx={props.cx} cy={props.cy} r={4} fill="#AFA9EC" stroke="none" opacity={0.8} />;
              }}
            />
          )}
          <Brush
            dataKey="week"
            height={20}
            stroke="var(--accent)"
            fill="var(--surface-raised)"
            travellerWidth={8}
            startIndex={data.length <= 104 ? 0 : Math.max(0, data.length - 104)}
            endIndex={data.length - 1}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

function R3SequenceChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const rawTournaments = spec.tournaments;
  const tournaments = normalizeTournaments(rawTournaments);
  const effectiveTour = spec.tour || resolved.tour || 'ATP';
  // R3 temporal separation (Riv.24 bis): the /sequence/risk endpoint now
  // requires target_season + data_season. Backend injects both into spec
  // when emitting the r3sequence chart; keep fallbacks defensively in case
  // spec arrives from an older backend build.
  const targetSeason = spec.target_season;
  const dataSeason = spec.data_season;

  useEffect(() => {
    resolvePlayer(player, fallback).then(setResolved);
  }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name || !tournaments) return;
    const params = new URLSearchParams({
      player: resolved.name,
      tournaments,
      tour: effectiveTour,
    });
    if (targetSeason) params.set('target_season', String(targetSeason));
    if (dataSeason) params.set('data_season', String(dataSeason));
    fetch(`${API}/sequence/risk?${params.toString()}`)
      .then(r => r.json())
      .then(d => {
        if (d.sequence) {
          setData(d.sequence.filter(s => !s.error).map(s => ({
            name: s.tourney_name,
            risk_score: s.sequence_risk_score,
            display_score: s.sequence_risk_score < 0.15 ? 0.15 : s.sequence_risk_score,
            tier: s.sequence_risk_tier,
          })));
        }
      })
      .catch(() => {});
  }, [resolved, tournaments, effectiveTour, targetSeason, dataSeason]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">R3 Sequence Risk — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 50 + 60)}>
        <BarChart data={data} layout="vertical" margin={{top:5, right:40, bottom:5, left:0}}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-primary)' }} width={140} />
          <Tooltip
            formatter={(value, name, props) => [
              props.payload.risk_score.toFixed(2),
              'Sequence Risk'
            ]}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }}
          />
          <ReferenceLine
            x={3}
            stroke="#FBBF24"
            strokeDasharray="4 4"
            label={{ value: 'threshold 3.0', position: 'insideTopRight', fill: '#FBBF24', fontSize: 9 }}
          />
          <Bar dataKey="display_score" name="Risk Score" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={RISK_COLORS[entry.tier] || 'var(--accent)'} />
            ))}
            <LabelList
              dataKey="risk_score"
              position="right"
              formatter={(v) => v.toFixed(2)}
              style={{ fill: 'var(--text-secondary)', fontSize: 10 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function ComparisonChartView({ spec }) {
  const [data1, setData1] = useState(null);
  const [data2, setData2] = useState(null);
  const players = (spec.players || '').split(',').map(s => s.trim());
  const season = spec.season || 2024;
  const p1 = players[0]; const p2 = players[1];

  useEffect(() => {
    if (!p1) return;
    fetch(`${API}/player/${encodeURIComponent(p1)}/chart/radar?season=${season}`)
      .then(r => r.json()).then(d => { if (d.dimensions) setData1(d.dimensions); }).catch(() => {});
  }, [p1, season]);

  useEffect(() => {
    if (!p2) return;
    fetch(`${API}/player/${encodeURIComponent(p2)}/chart/radar?season=${season}`)
      .then(r => r.json()).then(d => { if (d.dimensions) setData2(d.dimensions); }).catch(() => {});
  }, [p2, season]);

  if (!data1 || !data2) return null;

  const merged = data1.map((d, i) => ({
    axis: d.axis, [p1]: d.value, [p2]: data2[i]?.value || 0,
  }));

  return (
    <div className="chart-container">
      <div className="chart-title">Comparison — {p1} vs {p2}</div>
      <ResponsiveContainer width="100%" height={300}>
        <RadarChart data={merged} margin={{top:10, right:20, bottom:10, left:20}}>
          <PolarGrid stroke="var(--border)" />
          <PolarAngleAxis dataKey="axis" tick={{ fill: 'var(--text-secondary)', fontSize: 11 }} />
          <PolarRadiusAxis domain={[0, 10]} tick={false} axisLine={false} />
          <Radar name={p1} dataKey={p1} stroke="var(--accent-bright)" fill="var(--accent-bright)" fillOpacity={0.2} strokeWidth={2} />
          <Radar name={p2} dataKey={p2} stroke="#FBBF24" fill="#FBBF24" fillOpacity={0.15} strokeWidth={2} />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );
}

const CATEGORY_COLORS = {
  GS: '#7B74E0',
  M1000: '#AFA9EC',
  WTA1000: '#AFA9EC',
  ATP500: '#4ADE80',
  WTA500: '#4ADE80',
  ATP250: '#FBBF24',
  WTA250: '#FBBF24',
  Finals: '#7B74E0',
};

function CalendarLoadChartView({ spec }) {
  const [hover, setHover] = useState(null);
  const [weeks, setWeeks] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => {
    resolvePlayer(player, fallback).then(setResolved);
  }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/calendar?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.weeks) setWeeks(d.weeks); })
      .catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!weeks) return null;

  const cols = 7;
  const played = weeks.filter(w => w.tournament).length;

  return (
    <div className="chart-container">
      <div className="chart-title">Calendar Load — {resolved.name} {season} ({played} tournaments)</div>
      <div style={{ position: 'relative' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: isMobile() ? 2 : 3, maxWidth: isMobile() ? '100%' : 280 }}>
          {weeks.map(w => {
            const color = w.tournament ? (CATEGORY_COLORS[w.category] || '#F87171') : '#1A1730';
            return (
              <div
                key={w.week}
                onMouseEnter={() => setHover(w)}
                onMouseLeave={() => setHover(null)}
                style={{
                  width: '100%', aspectRatio: '1', borderRadius: 3,
                  background: color,
                  opacity: w.tournament ? 1 : 0.4,
                  cursor: w.tournament ? 'pointer' : 'default',
                }}
              />
            );
          })}
        </div>
        {hover && hover.tournament && (
          <div style={{
            position: 'absolute', top: -30, left: 0,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '4px 10px', fontSize: 11,
            color: 'var(--text-primary)', whiteSpace: 'nowrap', zIndex: 10,
          }}>
            Wk {hover.week} — {hover.tournament} ({hover.category})
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
          {Object.entries(CATEGORY_COLORS).filter(([k]) => !k.startsWith('WTA')).map(([cat, col]) => (
            <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: 'var(--text-secondary)' }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: col }} />
              {cat}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const SEASON_BAR_COLORS = {
  'Overall': '#7B74E0',
  'Load Capacity': '#4ADE80',
  'Availability': '#AFA9EC',
  'Recovery': '#FBBF24',
};

function SeasonBarsChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/season_bars?tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.bars && d.bars.length > 0) setData(d.bars); })
      .catch(() => {});
  }, [resolved, effectiveTour]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">Season Profile — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="season" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={30} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
          {!isMobile() && <Legend wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }} iconSize={8} />}
          {Object.entries(SEASON_BAR_COLORS).map(([key, color]) => (
            <Bar key={key} dataKey={key} fill={color} opacity={0.85} barSize={isMobile() ? 5 : 8} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function TournamentBarsChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/tournament_bars?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.bars && d.bars.length > 0) setData(d.bars); })
      .catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">Tournament Load — {resolved.name} {season}</div>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * (isMobile() ? 22 : 28) + 60)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: isMobile() ? 20 : 40, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="label" width={isMobile() ? 80 : 130}
            tick={{ fontSize: isMobile() ? 9 : 10, fill: 'var(--text-primary)' }}
            tickFormatter={v => isMobile() && v.length > 12 ? v.slice(0, 12) + '\u2026' : v} />
          <Tooltip
            formatter={(value, name, props) => {
              const p = props.payload;
              return [
                `${value} pts | ${p.estimated_minutes || '?'} min | risk ${((p.estimated_break_risk || 0) * 100).toFixed(1)}% | ${p.surface}`,
                p.category,
              ];
            }}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
          />
          <Bar dataKey="estimated_points" name="Points" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={CATEGORY_COLORS[entry.category] || '#F87171'} />
            ))}
            <LabelList dataKey="estimated_points" position="right" formatter={v => v ? Math.round(v) : ''} style={{ fill: 'var(--text-secondary)', fontSize: 9 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function SurfaceRiskChartView({ spec }) {
  const [data, setData] = useState(null);
  const tour = spec.tour || 'ATP';
  const season = spec.season || 2024;

  useEffect(() => {
    fetch(`${API}/chart/surface_risk?tour=${tour}&season=${season}`)
      .then(r => r.json())
      .then(d => { if (d.surfaces && d.surfaces.length > 0) setData(d.surfaces); })
      .catch(() => {});
  }, [tour, season]);

  if (!data) return null;

  const maxRate = Math.max(...data.map(d => d.break_rate || 0));

  return (
    <div className="chart-container">
      <div className="chart-title">Surface Break Risk — {tour} {season}</div>
      <ResponsiveContainer width="100%" height={220}>
        <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="surface" tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
          <YAxis tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={40} tickFormatter={v => `${(v * 100).toFixed(0)}%`} />
          <Tooltip
            formatter={(value, name, props) => {
              const p = props.payload;
              return [`Break: ${(value * 100).toFixed(1)}% | Ace: ${((p.ace_rate || 0) * 100).toFixed(1)}% | Recovery: ${p.recovery || '?'}/10`, p.surface];
            }}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
          />
          <Bar dataKey="break_rate" name="Break Risk" radius={[4, 4, 0, 0]}>
            {data.map((entry, i) => {
              const ratio = maxRate > 0 ? (entry.break_rate || 0) / maxRate : 0;
              const color = ratio > 0.7 ? '#F87171' : ratio > 0.4 ? '#FBBF24' : '#4ADE80';
              return <Cell key={i} fill={color} opacity={0.85} />;
            })}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


const PLAYER_COLORS = ['#7B74E0', '#4ADE80', '#FBBF24', '#F87171', '#AFA9EC'];

function BreakTrendChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/break_trend?tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.trends && d.trends.length > 0) setData(d.trends); })
      .catch(() => {});
  }, [resolved, effectiveTour]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">Break History — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={260}>
        <LineChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="season" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={30} />
          <Tooltip
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
            formatter={(value, name, props) => {
              if (name === 'avg_weeks_lost') return [`${value} wks`, 'Avg weeks lost'];
              return [value, name];
            }}
          />
          {!isMobile() && <Legend wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }} iconSize={8} />}
          <Line type="monotone" dataKey="breaks_8w" name="Breaks ≥8w" stroke="#F87171" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="breaks_4w" name="Breaks 4-7w" stroke="#FBBF24" strokeWidth={2} dot={{ r: 3 }} />
          <Line type="monotone" dataKey="retirements" name="Retirements" stroke="#AFA9EC" strokeWidth={1.5} strokeDasharray="4 4" dot={{ r: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}


function PhysicalComparisonChartView({ spec }) {
  const [data, setData] = useState(null);
  const playersParam = spec.players || '';
  const tour = spec.tour || 'ATP';

  useEffect(() => {
    if (!playersParam) return;
    fetch(`${API}/chart/physical_comparison?players=${encodeURIComponent(playersParam)}&tour=${tour}`)
      .then(r => r.json())
      .then(d => { if (d.players && d.players.length > 0) setData(d.players); })
      .catch(() => {});
  }, [playersParam, tour]);

  if (!data) return null;

  const metrics = ['overall', 'load_capacity', 'availability', 'recovery'];
  const metricLabels = { overall: 'Overall', load_capacity: 'Load Cap', availability: 'Availability', recovery: 'Recovery' };

  // Single player fallback: render horizontal breakdown instead of grouped bars
  if (data.length === 1) {
    const p = data[0];
    const singleData = metrics.map(m => ({
      signal: metricLabels[m], value: p[m],
    }));
    return (
      <div className="chart-container">
        <div className="chart-title">Physical Profile — {p.player}</div>
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={singleData} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 0 }}>
            <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
            <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
            <YAxis type="category" dataKey="signal" width={isMobile() ? 80 : 100} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
            <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
            <Bar dataKey="value" radius={[0, 4, 4, 0]}>
              {singleData.map((entry, i) => (
                <Cell key={i} fill={Object.values(MORPH_COLORS)[i % Object.values(MORPH_COLORS).length]} opacity={0.85} />
              ))}
              <LabelList dataKey="value" position="right" formatter={v => v != null ? v.toFixed(1) : ''} style={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  const chartData = metrics.map(m => {
    const entry = { metric: metricLabels[m] };
    data.forEach(p => { entry[p.player] = p[m]; });
    return entry;
  });

  return (
    <div className="chart-container">
      <div className="chart-title">Physical Comparison — {tour}</div>
      <ResponsiveContainer width="100%" height={Math.max(200, metrics.length * 50 + 60)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 5, right: isMobile() ? 20 : 40, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="metric" width={isMobile() ? 80 : 140} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
          <Legend wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }} iconSize={8} />
          {data.map((p, i) => (
            <Bar key={p.player} dataKey={p.player} fill={i === 0 ? '#7B74E0' : '#4ADE80'} opacity={0.85} barSize={isMobile() ? 8 : 12} radius={[0, 4, 4, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function ScatterLoadChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/scatter_load?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.points && d.points.length > 0) setData(d.points); })
      .catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!data) return null;

  const showLabel = new Set(['GS', 'M1000', 'WTA1000']);

  return (
    <div className="chart-container">
      <div className="chart-title">Load vs Yield — {resolved.name} {season}</div>
      <ResponsiveContainer width="100%" height={isMobile() ? 220 : 300}>
        <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" dataKey="minutes" name="Minutes" tick={{ fontSize: isMobile() ? 8 : 10, fill: 'var(--text-secondary)' }}
            label={isMobile() ? undefined : { value: 'Minutes (load)', position: 'insideBottom', offset: -5, fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="number" dataKey="points" name="Points" tick={{ fontSize: isMobile() ? 8 : 10, fill: 'var(--text-secondary)' }} width={35}
            label={isMobile() ? undefined : { value: 'Points (yield)', angle: -90, position: 'insideLeft', fontSize: 10, fill: 'var(--text-secondary)' }} />
          <ZAxis range={[40, 40]} />
          <Tooltip
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
            formatter={(value, name) => [typeof value === 'number' ? Math.round(value) : value, name]}
            labelFormatter={() => ''}
            content={({ payload }) => {
              if (!payload || !payload[0]) return null;
              const p = payload[0].payload;
              return (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: 'var(--text-primary)' }}>
                  <div style={{ fontWeight: 600 }}>{p.tournament}</div>
                  <div>{Math.round(p.points)} pts | {Math.round(p.minutes)} min | {p.surface}</div>
                  <div>Break risk: {((p.break_risk || 0) * 100).toFixed(1)}%</div>
                </div>
              );
            }}
          />
          <Scatter data={data} shape={(props) => {
            const d = props.payload;
            const color = CATEGORY_COLORS[d.category] || '#F87171';
            return (
              <g>
                <circle cx={props.cx} cy={props.cy} r={6} fill={color} opacity={0.85} stroke="none" />
                {showLabel.has(d.category) && (
                  <text x={props.cx + 9} y={props.cy + 3} fontSize={9} fill="var(--text-secondary)">{d.tournament.split(' ')[0]}</text>
                )}
              </g>
            );
          }} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}


const MORPH_COLORS = { overall: '#7B74E0', load_capacity: '#4ADE80', availability: '#FBBF24' };
const SIGNAL_GROUP_COLORS = { composite: '#7B74E0', risk: '#F87171', durability: '#4ADE80' };

function MorphologyBarsChartView({ spec }) {
  const [data, setData] = useState(null);
  const groupBy = spec.group_by || 'height_bucket';
  const tour = spec.tour || 'ATP';
  const season = spec.season || 2024;

  useEffect(() => {
    fetch(`${API}/chart/morphology_bars?group_by=${groupBy}&tour=${tour}&season=${season}`)
      .then(r => r.json()).then(d => { if (d.bars && d.bars.length > 0) setData(d.bars); }).catch(() => {});
  }, [groupBy, tour, season]);

  if (!data) return null;
  return (
    <div className="chart-container">
      <div className="chart-title">Morphology — {groupBy} × Performance {tour} {season}</div>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="group" tick={{ fontSize: isMobile() ? 8 : 10, fill: 'var(--text-secondary)' }} />
          <YAxis domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={30} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
            formatter={(v, name, props) => [v, name]}
            labelFormatter={label => { const d = data.find(x => x.group === label); return `${label} (${d?.player_count || '?'} players)`; }} />
          {!isMobile() && <Legend wrapperStyle={{ fontSize: 10 }} iconSize={8} />}
          {Object.entries(MORPH_COLORS).map(([key, color]) => (
            <Bar key={key} dataKey={key} name={key.replace('_', ' ')} fill={color} opacity={0.85} barSize={isMobile() ? 5 : 10} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function CountryBarsChartView({ spec }) {
  const [data, setData] = useState(null);
  const tour = spec.tour || 'ATP';
  const season = spec.season || 2024;

  useEffect(() => {
    fetch(`${API}/chart/country_bars?tour=${tour}&season=${season}`)
      .then(r => r.json()).then(d => { if (d.bars && d.bars.length > 0) setData(d.bars); }).catch(() => {});
  }, [tour, season]);

  if (!data) return null;
  return (
    <div className="chart-container">
      <div className="chart-title">Country Risk — {tour} {season}</div>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * 24 + 40)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 30, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="country" width={isMobile() ? 40 : 50}
            tick={{ fontSize: 10, fill: 'var(--text-primary)' }}
            tickFormatter={v => isMobile() ? v.slice(0, 3) : v} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
            formatter={(v, name, props) => [`${v.toFixed(3)} (${props.payload.players} players)`, 'Break rate']} />
          <Bar dataKey="value" fill="#AFA9EC" opacity={0.85} radius={[0, 4, 4, 0]}>
            <LabelList dataKey="value" position="right" formatter={v => v ? v.toFixed(2) : ''} style={{ fill: 'var(--text-secondary)', fontSize: 9 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function RiskTimelineChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);
  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/risk_timeline?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json()).then(d => { if (d.timeline && d.timeline.length > 0) setData(d.timeline); }).catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!data) return null;
  return (
    <div className="chart-container">
      <div className="chart-title">Risk Timeline — {resolved.name} {season}</div>
      <ResponsiveContainer width="100%" height={260}>
        <ComposedChart data={data} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="tournament" tick={{ fontSize: isMobile() ? 7 : 9, fill: 'var(--text-secondary)' }}
            tickFormatter={v => isMobile() && v.length > 8 ? v.slice(0, 8) + '\u2026' : v} interval={0} angle={isMobile() ? -45 : 0} textAnchor={isMobile() ? 'end' : 'middle'} height={isMobile() ? 60 : 30} />
          <YAxis yAxisId="left" allowDecimals={false} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={25} />
          <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 9, fill: 'var(--text-secondary)' }} width={25} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }} />
          <Bar yAxisId="right" dataKey="matches" name="Matches" fill="var(--surface-raised)" opacity={0.5} barSize={12} />
          <Line yAxisId="left" type="monotone" dataKey="break_8w" name="Break ≥8w" stroke="#F87171" strokeWidth={2} dot={{ r: 4, fill: '#F87171' }} />
          <Line yAxisId="left" type="monotone" dataKey="break_4w" name="Break ≥4w" stroke="#FBBF24" strokeWidth={1.5} dot={{ r: 3, fill: '#FBBF24' }} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}


function SignalBreakdownChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const season = spec.season || 2024;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => { resolvePlayer(player, fallback).then(setResolved); }, [player, fallback]);
  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/signal_breakdown?season=${season}&tour=${effectiveTour}`)
      .then(r => r.json()).then(d => { if (d.signals && d.signals.length > 0) setData(d.signals); }).catch(() => {});
  }, [resolved, season, effectiveTour]);

  if (!data) return null;
  return (
    <div className="chart-container">
      <div className="chart-title">Signal Breakdown — {resolved.name} {season}</div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 36 + 40)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 40, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="signal" width={isMobile() ? 80 : 100} tick={{ fontSize: 11, fill: 'var(--text-primary)' }} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)' }} />
          <Bar dataKey="value" name="Score" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={SIGNAL_GROUP_COLORS[entry.group] || '#AFA9EC'} opacity={0.85} />
            ))}
            <LabelList dataKey="value" position="right" formatter={v => v != null ? v.toFixed(1) : ''} style={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


function ScatterComparisonChartView({ spec }) {
  const [data, setData] = useState(null);
  const playersParam = spec.players || '';
  const tour = spec.tour || 'ATP';
  const xMetric = spec.x_metric || 'load_capacity_index_10';
  const yMetric = spec.y_metric || 'overall_index_10';

  useEffect(() => {
    if (!playersParam) return;
    fetch(`${API}/chart/scatter_comparison?players=${encodeURIComponent(playersParam)}&x_metric=${xMetric}&y_metric=${yMetric}&tour=${tour}`)
      .then(r => r.json()).then(d => { if (d.points && d.points.length > 0) setData(d.points); }).catch(() => {});
  }, [playersParam, xMetric, yMetric, tour]);

  if (!data) return null;
  const xLabel = xMetric.replace(/_index_10|_10/g, '').replace(/_/g, ' ');
  const yLabel = yMetric.replace(/_index_10|_10/g, '').replace(/_/g, ' ');

  return (
    <div className="chart-container">
      <div className="chart-title">Player Map — {tour}</div>
      <ResponsiveContainer width="100%" height={isMobile() ? 220 : 300}>
        <ScatterChart margin={{ top: 20, right: 20, bottom: 10, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" dataKey="x" name={xLabel} domain={[0, 10]}
            tick={{ fontSize: isMobile() ? 8 : 10, fill: 'var(--text-secondary)' }}
            label={isMobile() ? undefined : { value: xLabel, position: 'insideBottom', offset: -5, fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="number" dataKey="y" name={yLabel} domain={[0, 10]}
            tick={{ fontSize: isMobile() ? 8 : 10, fill: 'var(--text-secondary)' }} width={35}
            label={isMobile() ? undefined : { value: yLabel, angle: -90, position: 'insideLeft', fontSize: 10, fill: 'var(--text-secondary)' }} />
          <ZAxis range={[50, 50]} />
          <Tooltip content={({ payload }) => {
            if (!payload || !payload[0]) return null;
            const p = payload[0].payload;
            return (
              <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 6, padding: '6px 10px', fontSize: 11, color: 'var(--text-primary)' }}>
                <div style={{ fontWeight: 600 }}>{p.player}</div>
                <div>{xLabel}: {p.x} | {yLabel}: {p.y}</div>
              </div>
            );
          }} />
          <Scatter data={data} shape={(props) => {
            const idx = data.indexOf(props.payload);
            const color = PLAYER_COLORS[idx % PLAYER_COLORS.length];
            return (
              <g>
                <circle cx={props.cx} cy={props.cy} r={7} fill={color} opacity={0.85} />
                <text x={props.cx} y={props.cy - 12} textAnchor="middle" fontSize={isMobile() ? 8 : 10} fill="var(--text-primary)">{props.payload.player.split(' ').pop()}</text>
              </g>
            );
          }} />
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  );
}


const INJURY_TYPE_COLORS = {
  acuto: '#F87171',
  cronico: '#FBBF24',
  affaticamento: '#AFA9EC',
  precauzionale: '#4ADE80',
  post_chirurgico: '#7B74E0',
};

function InjuryHeatmapChartView({ spec }) {
  const [data, setData] = useState(null);
  const tour = spec.tour || 'ATP';
  const dimension = spec.dimension || 'surface';

  useEffect(() => {
    fetch(`${API}/chart/injury_heatmap?tour=${tour}&dimension=${dimension}`)
      .then(r => r.json())
      .then(d => { if (d.cells && d.cells.length > 0) setData(d); })
      .catch(() => {});
  }, [tour, dimension]);

  if (!data) return null;

  const bodyParts = [...new Set(data.cells.map(c => c.body_part))];
  const rawDimValues = [...new Set(data.cells.map(c => c.dimension_value))];
  const HT_ORD = ['<170','170-175','175-180','180-185','<185','185-190','185+','190-195','195-200','200+'];
  const AG_ORD = ['U21','21-24','25-28','29-32','33+'];
  const RK_ORD = ['Top10','Top11-50','Top51-100','Outside100'];
  const dimOrder = (v) => { let i = HT_ORD.indexOf(v); if (i !== -1) return i; i = AG_ORD.indexOf(v); if (i !== -1) return i + 100; i = RK_ORD.indexOf(v); if (i !== -1) return i + 200; return 999; };
  const dimValues = rawDimValues.sort((a, b) => dimOrder(a) - dimOrder(b));
  const cellMap = {};
  data.cells.forEach(c => { cellMap[`${c.body_part}|${c.dimension_value}`] = c; });

  const cellSize = isMobile() ? 44 : 56;
  const labelW = isMobile() ? 70 : 100;

  function cellColor(pct) {
    if (pct == null) return 'var(--surface)';
    if (pct > 0.5) return '#F87171';
    if (pct > 0) return '#FBBF24';
    if (pct > -0.3) return '#4ADE80';
    return '#34D399';
  }

  return (
    <div className="chart-container">
      <div className="chart-title">Injury Risk Map — {tour} × {dimension}</div>
      <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <div style={{ display: 'grid', gridTemplateColumns: `${labelW}px repeat(${dimValues.length}, ${cellSize}px)`, gap: 2, fontSize: isMobile() ? 9 : 11 }}>
          <div />
          {dimValues.map(dv => (
            <div key={dv} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 2, fontWeight: 600 }}>{dv}</div>
          ))}
          {bodyParts.map(bp => (
            <React.Fragment key={bp}>
              <div style={{ color: 'var(--text-primary)', padding: '4px 4px 4px 0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{bp}</div>
              {dimValues.map(dv => {
                const cell = cellMap[`${bp}|${dv}`];
                if (!cell) return <div key={dv} style={{ background: 'var(--surface)', borderRadius: 4 }} />;
                return (
                  <div key={dv} title={`${bp} × ${dv}\nRecovery: ${cell.avg_recovery_weeks}w\nDelta: ${cell.modifier_delta_pct > 0 ? '+' : ''}${(cell.modifier_delta_pct * 100).toFixed(0)}%\nn=${cell.sample_size}`}
                    style={{
                      background: cellColor(cell.modifier_delta_pct),
                      opacity: cell.sample_size < 5 ? 0.3 : 0.85,
                      borderRadius: 4, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#0E0C1E', fontWeight: 600, fontSize: isMobile() ? 9 : 11,
                    }}>
                    {cell.avg_recovery_weeks}w
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
}

function InjuryProfileChartView({ spec }) {
  const [data, setData] = useState(null);
  const [resolved, setResolved] = useState({ name: null, tour: null });
  const player = spec.player;
  const fallback = spec._fallbackPlayer;
  const effectiveTour = spec.tour || resolved.tour || 'ATP';

  useEffect(() => {
    resolvePlayer(player, fallback).then(setResolved);
  }, [player, fallback]);

  useEffect(() => {
    if (!resolved.name) return;
    fetch(`${API}/player/${encodeURIComponent(resolved.name)}/chart/injury_profile?tour=${effectiveTour}`)
      .then(r => r.json())
      .then(d => { if (d.injuries && d.injuries.length > 0) setData(d.injuries); })
      .catch(() => {});
  }, [resolved, effectiveTour]);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">Injury Profile — {resolved.name}</div>
      <ResponsiveContainer width="100%" height={Math.max(180, data.length * 40 + 60)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: 50, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} />
          <YAxis type="category" dataKey="body_part" tick={{ fontSize: 11, fill: 'var(--text-primary)' }} width={isMobile() ? 80 : 110} />
          <Tooltip
            formatter={(value, name, props) => {
              const p = props.payload;
              return [`${value} episodes | ${p.avg_recovery || '?'}w avg${p.retirements > 0 ? ` | ${p.retirements} ret` : ''}`, p.body_part];
            }}
            contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }}
          />
          <Bar dataKey="occurrences" name="Injuries" radius={[0, 4, 4, 0]}>
            {data.map((entry, i) => (
              <Cell key={i} fill={INJURY_TYPE_COLORS[entry.injury_type] || 'var(--accent)'} opacity={0.85} />
            ))}
            <LabelList
              dataKey="avg_recovery"
              position="right"
              formatter={v => v ? `${v}w` : ''}
              style={{ fill: 'var(--text-secondary)', fontSize: 10 }}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function InjuryRankingGradientChartView({ spec }) {
  const [data, setData] = useState(null);
  const tour = spec.tour || 'ATP';

  useEffect(() => {
    fetch(`${API}/chart/injury_ranking_gradient?tour=${tour}`)
      .then(r => r.json())
      .then(d => {
        if (d.data && d.data.length > 0) {
          const flat = [];
          d.data.forEach(bucket => {
            bucket.injuries.slice(0, 4).forEach(inj => {
              flat.push({ rank_bucket: bucket.rank_bucket, body_part: inj.body_part, avg_recovery_weeks: inj.avg_recovery_weeks, sample_size: inj.sample_size });
            });
          });
          setData(flat);
        }
      })
      .catch(() => {});
  }, [tour]);

  if (!data) return null;

  const rankBuckets = [...new Set(data.map(d => d.rank_bucket))];
  const bodyParts = [...new Set(data.map(d => d.body_part))].slice(0, 4);
  const BP_COLORS = ['#7B74E0', '#4ADE80', '#FBBF24', '#F87171'];

  const grouped = rankBuckets.map(rb => {
    const row = { rank_bucket: rb };
    data.filter(d => d.rank_bucket === rb).forEach(d => { row[d.body_part] = d.avg_recovery_weeks; });
    return row;
  });

  return (
    <div className="chart-container">
      <div className="chart-title">Recovery by Ranking — {tour}</div>
      <ResponsiveContainer width="100%" height={isMobile() ? 220 : 280}>
        <BarChart data={grouped} margin={{ top: 5, right: 10, bottom: 5, left: -10 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis dataKey="rank_bucket" tick={{ fontSize: isMobile() ? 9 : 11, fill: 'var(--text-primary)' }} />
          <YAxis tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} width={35} label={isMobile() ? undefined : { value: 'weeks', angle: -90, position: 'insideLeft', fill: 'var(--text-secondary)', fontSize: 10 }} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }} />
          {!isMobile() && <Legend wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }} iconSize={8} />}
          {bodyParts.map((bp, i) => (
            <Bar key={bp} dataKey={bp} name={bp} fill={BP_COLORS[i % BP_COLORS.length]} opacity={0.85} barSize={isMobile() ? 8 : 14} radius={[4, 4, 0, 0]} />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

function InjuryTourComparisonChartView({ spec }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/chart/injury_tour_comparison`)
      .then(r => r.json())
      .then(d => { if (d.data && d.data.length > 0) setData(d.data); })
      .catch(() => {});
  }, []);

  if (!data) return null;

  return (
    <div className="chart-container">
      <div className="chart-title">ATP vs WTA Injury Profile</div>
      <ResponsiveContainer width="100%" height={Math.max(200, data.length * (isMobile() ? 28 : 36) + 60)}>
        <BarChart data={data} layout="vertical" margin={{ top: 5, right: isMobile() ? 10 : 30, bottom: 5, left: 0 }}>
          <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" />
          <XAxis type="number" tick={{ fontSize: 10, fill: 'var(--text-secondary)' }} label={isMobile() ? undefined : { value: 'avg recovery (weeks)', position: 'insideBottom', fill: 'var(--text-secondary)', fontSize: 10, offset: -5 }} />
          <YAxis type="category" dataKey="body_part" tick={{ fontSize: isMobile() ? 9 : 11, fill: 'var(--text-primary)' }} width={isMobile() ? 70 : 100} />
          <Tooltip contentStyle={{ background: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--text-primary)', fontSize: 11 }} />
          <Legend wrapperStyle={{ fontSize: 10, color: 'var(--text-secondary)' }} iconSize={8} />
          <Bar dataKey="atp_recovery" name="ATP" fill="#7B74E0" opacity={0.85} barSize={isMobile() ? 6 : 10} radius={[0, 4, 4, 0]} />
          <Bar dataKey="wta_recovery" name="WTA" fill="#F87171" opacity={0.85} barSize={isMobile() ? 6 : 10} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}


export default function ChartRenderer({ spec, player }) {
  if (!spec || !spec.type) return null;
  const fullName = player ? player.split(' (')[0] : null;
  const enriched = { ...spec, player: spec.player || fullName, _fallbackPlayer: fullName };

  switch (spec.type) {
    case 'radar': return <RadarChartView spec={enriched} />;
    case 'timeline': return <TimelineChartView spec={enriched} />;
    case 'r3sequence': return <R3SequenceChartView spec={enriched} />;
    case 'comparison': return <ComparisonChartView spec={enriched} />;
    case 'calendar_load': return <CalendarLoadChartView spec={enriched} />;
    case 'season_bars': return <SeasonBarsChartView spec={enriched} />;
    case 'tournament_bars': return <TournamentBarsChartView spec={enriched} />;
    case 'surface_risk': return <SurfaceRiskChartView spec={enriched} />;
    case 'break_trend': return <BreakTrendChartView spec={enriched} />;
    case 'physical_comparison': return <PhysicalComparisonChartView spec={enriched} />;
    case 'scatter_load': return <ScatterLoadChartView spec={enriched} />;
    case 'morphology_bars': return <MorphologyBarsChartView spec={enriched} />;
    case 'country_bars': return <CountryBarsChartView spec={enriched} />;
    case 'risk_timeline': return <RiskTimelineChartView spec={enriched} />;
    case 'signal_breakdown': return <SignalBreakdownChartView spec={enriched} />;
    case 'scatter_comparison': return <ScatterComparisonChartView spec={enriched} />;
    case 'injury_heatmap': return <InjuryHeatmapChartView spec={enriched} />;
    case 'injury_profile': return <InjuryProfileChartView spec={enriched} />;
    case 'injury_ranking_gradient': return <InjuryRankingGradientChartView spec={enriched} />;
    case 'injury_tour_comparison': return <InjuryTourComparisonChartView spec={enriched} />;
    default: return null;
  }
}
